import { supabase } from './supabase'
import type { User } from './types'

interface PhoneOTPCredentials {
  phone: string
  otp: string
}

export class AuthService {
  /**
   * Send OTP to phone number
   */
  async sendOTP(phone: string): Promise<void> {
    try {
      const { error } = await supabase.auth.signInWithOtp({
        phone: phone,
        options: {
          channel: 'sms',
        },
      })

      if (error) {
        console.error('Failed to send OTP:', error)
        throw error
      }
    } catch (error) {
      console.error('Send OTP failed:', error)
      throw error
    }
  }

  /**
   * Verify OTP and sign in user
   */
  async verifyOTP(credentials: PhoneOTPCredentials): Promise<User> {
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        phone: credentials.phone,
        token: credentials.otp,
        type: 'sms',
      })

      if (error) {
        console.error('OTP verification failed:', error)
        throw error
      }

      if (!data.user || !data.session) {
        throw new Error('Authentication failed')
      }

      // Convert Supabase user to our User type
      const user: User = {
        id: data.user.id,
        email: data.user.email || '',
        name: data.user.user_metadata?.name || 'User',
        avatar: data.user.user_metadata?.avatar_url,
        createdAt: data.user.created_at || new Date().toISOString(),
        updatedAt: data.user.updated_at || new Date().toISOString(),
      }

      return user
    } catch (error) {
      console.error('OTP verification failed:', error)
      throw error
    }
  }

  /**
   * Logout the current user
   */
  async logout(): Promise<void> {
    try {
      const { error } = await supabase.auth.signOut()
      if (error) {
        console.error('Logout failed:', error)
        throw error
      }
    } catch (error) {
      console.error('Logout failed:', error)
      throw error
    }
  }

  /**
   * Get current user profile
   */
  async getCurrentUser(): Promise<User | null> {
    try {
      const {
        data: { user },
        error,
      } = await supabase.auth.getUser()

      if (error) {
        console.error('Failed to get current user:', error)
        throw error
      }

      if (!user) {
        return null
      }

      // Convert Supabase user to our User type
      return {
        id: user.id,
        email: user.email || '',
        name: user.user_metadata?.name || 'User',
        avatar: user.user_metadata?.avatar_url,
        createdAt: user.created_at || new Date().toISOString(),
        updatedAt: user.updated_at || new Date().toISOString(),
      }
    } catch (error) {
      console.error('Failed to get current user:', error)
      throw error
    }
  }

  /**
   * Get current session
   */
  async getCurrentSession() {
    try {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession()

      if (error) {
        console.error('Failed to get session:', error)
        throw error
      }

      return session
    } catch (error) {
      console.error('Failed to get session:', error)
      throw error
    }
  }

  /**
   * Initialize authentication - check for existing session
   */
  async initializeAuth() {
    try {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession()

      if (error) {
        console.error('Failed to initialize auth:', error)
        return null
      }

      return session
    } catch (error) {
      console.error('Failed to initialize auth:', error)
      return null
    }
  }
}

// Export a singleton instance
export const authService = new AuthService()
